import React, { useState, useEffect, useMemo } from "react";
import { GamificationService } from "../../services/gamificationService";
import { Achievement } from "../../types/education";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import {
  Trophy,
  Star,
  Flame,
  Target,
  Zap,
  Crown,
} from "lucide-react";

interface AchievementDisplayProps {
  achievement: Achievement;
  isNew?: boolean;
  onClick?: () => void;
}

export const AchievementDisplay: React.FC<AchievementDisplayProps> = ({
  achievement,
  isNew = false,
  onClick,
}) => {
  const gamificationService = useMemo(() => new GamificationService(), []);
  const rarityColor = gamificationService.getRarityColor(achievement.rarity);

  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md ${
        isNew ? "ring-2 ring-blue-500 ring-offset-2" : ""
      }`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{achievement.icon}</div>
            <div>
              <CardTitle className="text-lg">{achievement.title}</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
                <Badge
                  variant="outline"
                  style={{ borderColor: rarityColor, color: rarityColor }}
                >
                  {achievement.rarity}
                </Badge>
                <Badge variant="secondary">{achievement.points} pts</Badge>
              </div>
            </div>
          </div>
          {achievement.unlocked && (
            <div className="text-green-500">
              <Trophy className="h-5 w-5" />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-sm">
          {achievement.description}
        </CardDescription>
        {achievement.unlocked && achievement.unlockedAt && (
          <div className="mt-2 text-xs text-muted-foreground">
            Unlocked {new Date(achievement.unlockedAt).toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface AchievementGridProps {
  achievements: Achievement[];
  onAchievementClick?: (achievement: Achievement) => void;
}

export const AchievementGrid: React.FC<AchievementGridProps> = ({
  achievements,
  onAchievementClick,
}) => {
  const [filter, setFilter] = useState<"all" | "unlocked" | "locked">("all");
  const [category, setCategory] = useState<string>("all");

  const filteredAchievements = achievements.filter((achievement) => {
    if (filter === "unlocked" && !achievement.unlocked) return false;
    if (filter === "locked" && achievement.unlocked) return false;
    if (category !== "all" && achievement.category !== category) return false;
    return true;
  });

  const categories = Array.from(new Set(achievements.map((a) => a.category)));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center space-x-1">
          <span className="text-xs font-medium text-muted-foreground">Status:</span>
          {(["all", "unlocked", "locked"] as const).map((filterOption) => (
            <Button
              key={filterOption}
              variant={filter === filterOption ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(filterOption)}
            >
              {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
            </Button>
          ))}
        </div>
        {categories.length > 0 && (
          <div className="flex items-center space-x-1">
            <span className="text-xs font-medium text-muted-foreground">Category:</span>
            <Button
              variant={category === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory("all")}
            >
              All
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={category === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(cat)}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Achievement Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredAchievements.map((achievement) => (
          <AchievementDisplay
            key={achievement.id}
            achievement={achievement}
            onClick={() => onAchievementClick?.(achievement)}
          />
        ))}
      </div>

      {filteredAchievements.length === 0 && (
        <div className="text-center py-8">
          <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium">No achievements found</h3>
          <p className="text-muted-foreground">
            Try adjusting your filters or complete more lessons to unlock
            achievements.
          </p>
        </div>
      )}
    </div>
  );
};

interface StreakDisplayProps {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string;
}

export const StreakDisplay: React.FC<StreakDisplayProps> = ({
  currentStreak,
  longestStreak,
  lastActivityDate,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (currentStreak > 0) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 500);
      return () => clearTimeout(timer);
    }
  }, [currentStreak]);

  const getStreakMessage = (count: number) => {
    if (count >= 30) return "Monthly Master! 🔥";
    if (count >= 7) return "Week Warrior! 💪";
    if (count >= 3) return "On a Roll! ⚡";
    if (count >= 1) return "Keep Going! 🎯";
    return "Start Your Journey! 🌟";
  };

  const daysSinceLastActivity = Math.floor(
    (Date.now() - new Date(lastActivityDate).getTime()) / (1000 * 60 * 60 * 24),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Flame className="h-5 w-5 mr-2" />
          Learning Streak
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Current Streak */}
          <div className="text-center">
            <div
              className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-red-500 text-white text-2xl font-bold mb-2 ${
                isAnimating ? "animate-pulse" : ""
              }`}
            >
              {currentStreak}
            </div>
            <div className="text-lg font-medium">
              {getStreakMessage(currentStreak)}
            </div>
            <div className="text-sm text-muted-foreground">
              {currentStreak} day{currentStreak !== 1 ? "s" : ""} in a row
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {longestStreak}
              </div>
              <div className="text-sm text-muted-foreground">
                Longest Streak
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-2xl font-bold ${
                  daysSinceLastActivity === 0
                    ? "text-green-600"
                    : "text-orange-600"
                }`}
              >
                {daysSinceLastActivity === 0
                  ? "Today"
                  : `${daysSinceLastActivity}d`}
              </div>
              <div className="text-sm text-muted-foreground">Last Activity</div>
            </div>
          </div>

          {/* Streak Progress */}
          {currentStreak > 0 && currentStreak < 30 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Next milestone</span>
                <span>
                  {currentStreak < 3
                    ? "3 days"
                    : currentStreak < 7
                      ? "7 days"
                      : currentStreak < 30
                        ? "30 days"
                        : "Master"}
                </span>
              </div>
              <Progress
                value={((currentStreak % 30) * 100) / 30}
                className="h-2"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

interface PointsDisplayProps {
  totalPoints: number;
  level: number;
  experience: number;
}

export const PointsDisplay: React.FC<PointsDisplayProps> = ({
  totalPoints,
  level,
  experience,
}) => {
  const gamificationService = useMemo(() => new GamificationService(), []);
  const { currentLevelExp, nextLevelExp } =
    gamificationService.calculateLevel(experience);
  const progressToNext =
    ((experience - currentLevelExp) / (nextLevelExp - currentLevelExp)) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Star className="h-5 w-5 mr-2" />
          Progress & Points
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Level Display */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 text-white text-xl font-bold mb-2">
              {level}
            </div>
            <div className="text-sm font-medium">Level {level}</div>
          </div>

          {/* Points */}
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">
              {totalPoints.toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">Total Points</div>
          </div>

          {/* Experience Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Level Progress</span>
              <span>
                {experience - currentLevelExp} /{" "}
                {nextLevelExp - currentLevelExp} XP
              </span>
            </div>
            <Progress value={progressToNext} className="h-3" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
